import React, { useCallback, useContext, useMemo } from 'react';
import isEqual from 'lodash/isEqual';

import TextBox from '../../../../components/forms/text-box';
import TextArea from '../../../../components/forms/text-area';
import DropDown from '../../../../components/forms/dropdown';
import DisplayTexts from '../../../action-journey-studio/common/components/display-texts';
import DynamicRows from '../../../../components/dynamic-rows';
import MultiSelect from '../../../../components/forms/multi-select-drop-down';
import SwitchComponent from '../../../../components/switch';
import { RadioButtonGroup } from '../../../../components/radio-button';
import { formDataRequestType, rawRequestType, requestTypes } from '../../../../consts/common-consts';
import SentToUserSwitch from '../../../action-journey-studio/common/components/send-to-user-switch';
import ResponseAIModify from '../../../action-journey-studio/common/components/response-ai-modify-select';
import PauseAfterExecutionSwitch from '../../../action-journey-studio/common/components/pause-after-execution-switch';
import AllowedFileTypes from '../../../action-journey-studio/common/components/allowed-file-types';

import { PlaybookContext } from '../../context/PlaybookContext';
import { ASK_INPUT, ASK_INPUT_LABEL, RUN_TARGET, STEP_KIND, knowledgeSelection } from '../../model/playbook-model';

/**
 * The overflow for a step - only what cannot sit on one line.
 *
 * Everything an author normally sets (the text, the variable, the tool) is
 * edited directly in the row. This opens from the "..." button and holds the
 * rest: the options behind a buttons/cards answer, a document's extraction
 * schema, API parameters/headers/body and response mapping, a function's
 * default input, a knowledge search's instruction and output format, retry
 * wording, and whether a RUN result is shown to the customer.
 */

const asOptions = map => Object.entries(map).map(([value, label]) => ({ value, label }));

function Field({ label, hint, children }) {
    return (
        <div className="space-y-1.5">
            {label && (
                <div className="flex items-baseline justify-between gap-2">
                    <label className="text-[12.5px] font-medium text-gray-600">{label}</label>
                    {hint && <span className="text-[11px] text-gray-400">{hint}</span>}
                </div>
            )}
            {children}
        </div>
    );
}

/** A name/value row list, as the API step's parameters, headers and form data use. */
function NameValueRows({ title, rows, onChange }) {
    return (
        <DynamicRows
            title={title}
            showTopDivider={false}
            showBottomDivider={false}
            data={(rows || []).map(inputs => ({ inputs }))}
            onChange={next => onChange(next.map(r => r.inputs))}
            rowsTemplate={(values, handleInputChange) => (
                <div className="flex w-full gap-2">
                    <TextBox rootClass="flex-1" label="Name" name="name" value={values.name || ''} onChange={handleInputChange} />
                    <TextBox rootClass="flex-1" label="Value" name="value" placeholder="{variable}" value={values.value || ''} onChange={handleInputChange} />
                </div>
            )}
        />
    );
}

/**
 * Whether a RUN result reaches the customer, and whether the AI rewords it
 * first. Off by default: a hidden result is still kept in the conversation, so
 * the agent can use it in later turns. Rewording only applies to a result that
 * is shown, so hiding it also turns rewording off - as the canvas does.
 */
function ResultDelivery({ cfg, set, allowModifyByAI = false }) {
    const hidden = cfg.skipNotify !== false;
    return (
        <>
            <SentToUserSwitch
                formData={cfg}
                setFormData={next => set(next.skipNotify
                    ? { skipNotify: true, responseModifyByAI: false }
                    : { skipNotify: false })}
                skipNotify={hidden}
            />
            {allowModifyByAI && !hidden && (
                <ResponseAIModify
                    responseModifyByAI={!!cfg.responseModifyByAI}
                    onChange={e => set({ responseModifyByAI: e.target.value })}
                />
            )}
        </>
    );
}

export default function StepSettings({ step, lookups, slotOptions }) {
    const { updateStepConfig } = useContext(PlaybookContext);

    const cfg = step.config || {};
    const set = patch => updateStepConfig(step.id, patch);
    const onField = e => set({ [e.target.name]: e.target.value });

    // DisplayTexts diffs this with isEqual, so it must be referentially stable.
    const validationTextOptions = useMemo(
        () => cfg.validationErrorDisplayTextOptions || [],
        [cfg.validationErrorDisplayTextOptions],
    );

    const inputType = cfg.inputType || ASK_INPUT.TEXT;
    const target = cfg.target || RUN_TARGET.PROMPT;
    const webhook = cfg.webhookConfig || {};
    const setWebhook = patch => set({ webhookConfig: { ...cfg.webhookConfig, ...patch } });

    // MultiSelect holds its own copy and echoes it back on mount and on every
    // prop change, so it needs a stable array and an equality guard - otherwise
    // merely opening the settings would mark the playbook as changed.
    const filterKb = useMemo(() => cfg.filterKb || [], [cfg.filterKb]);
    const knowledgeList = lookups?.knowledgeList;
    const onChangeKb = useCallback(ids => {
        if (isEqual(ids, filterKb)) return;
        set(knowledgeSelection(ids, knowledgeList));
    }, [filterKb, knowledgeList, step.id]); // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <div className="ml-[74px] mb-2 mt-0.5 space-y-4 rounded-xl border border-gray-200 bg-gray-50/60 p-3.5">
            {step.kind === STEP_KIND.ASK && (
                <>
                    <Field label="How should they answer?">
                        <DropDown
                            name="inputType"
                            value={inputType}
                            options={asOptions(ASK_INPUT_LABEL)}
                            onChange={onField}
                        />
                    </Field>

                    {(inputType === ASK_INPUT.BUTTONS || inputType === ASK_INPUT.CARDS) && (
                        <DynamicRows
                            title={inputType === ASK_INPUT.BUTTONS ? 'Options' : 'Cards'}
                            showTopDivider={false}
                            showBottomDivider={false}
                            data={(cfg.data || []).map(inputs => ({ inputs }))}
                            onChange={rows => set({ data: rows.map(r => r.inputs) })}
                            rowsTemplate={(values, handleInputChange) => (
                                <div className="flex w-full gap-2">
                                    <TextBox rootClass="flex-1" label="Label" name="title" value={values.title || ''} onChange={handleInputChange} />
                                    <TextBox rootClass="flex-1" label="Value" name="payload" value={values.payload || ''} onChange={handleInputChange} />
                                </div>
                            )}
                        />
                    )}

                    {inputType === ASK_INPUT.DOCUMENT && (
                        <>
                            <p className="text-[12px] leading-relaxed text-gray-500">
                                The customer gets an upload button. The file is read, the fields below are
                                extracted, and the variable holds {'{ raw, data }'} - use{' '}
                                <code className="text-gray-700">{`{${cfg.slot || 'variable'}.data.field}`}</code> in later steps.
                            </p>
                            <Field label="Allowed file types" hint="All of them if none selected">
                                <AllowedFileTypes
                                    showHeader={false}
                                    value={cfg.allowedFileTypes}
                                    onChange={allowedFileTypes => set({ allowedFileTypes })}
                                />
                            </Field>
                            <Field label="Fields to extract" hint="JSON, or plain English">
                                <TextArea
                                    name="extractionSchema"
                                    rows={5}
                                    placeholder={'{\n  "invoice_number": "the invoice reference",\n  "total_amount": "grand total including tax"\n}'}
                                    value={cfg.extractionSchema || ''}
                                    onChange={onField}
                                />
                            </Field>
                        </>
                    )}

                    {/* A rejected upload is re-asked with the runtime's own
                        file-type / missing-file message, so retry wording only
                        applies to typed and button answers. */}
                    {inputType !== ASK_INPUT.DOCUMENT && (
                        <DisplayTexts
                            title="If the answer is not valid"
                            fieldName="validationErrorDisplayTextOptions"
                            displayTopBorder={false}
                            parameterName={cfg.slot}
                            displayTextOptionsProps={validationTextOptions}
                            onChangeData={onField}
                            showGenerateTextByAI={false}
                            showResponseModifyByAI={false}
                        />
                    )}
                </>
            )}

            {step.kind === STEP_KIND.RUN && target === RUN_TARGET.FUNCTION && (
                <>
                    <Field label="Default input" hint="JSON, with {variable} allowed">
                        <TextArea
                            name="defaultInput"
                            rows={4}
                            value={cfg.defaultInput || '{}'}
                            onChange={onField}
                        />
                    </Field>
                    <ResultDelivery cfg={cfg} set={set} allowModifyByAI />
                </>
            )}

            {step.kind === STEP_KIND.RUN && target === RUN_TARGET.API && (
                <>
                    <NameValueRows
                        title="Query parameters"
                        rows={webhook.parameters}
                        onChange={parameters => setWebhook({ parameters })}
                    />

                    <NameValueRows
                        title="Headers"
                        rows={webhook.headers}
                        onChange={headers => setWebhook({ headers })}
                    />

                    {webhook.method !== 'GET' && (
                        <Field label="Request body">
                            <div className="flex justify-end">
                                <RadioButtonGroup
                                    name="requestType"
                                    selectedValue={webhook.requestType || rawRequestType.value}
                                    options={requestTypes}
                                    onChange={e => setWebhook({ requestType: e.target.value })}
                                />
                            </div>
                            {webhook.requestType === formDataRequestType.value ? (
                                <NameValueRows
                                    title="Form data"
                                    rows={webhook.requestFormData}
                                    onChange={requestFormData => setWebhook({ requestFormData })}
                                />
                            ) : (
                                <TextArea
                                    name="requestBody"
                                    rows={4}
                                    placeholder="JSON, with {variable} allowed"
                                    value={webhook.requestBody || '{}'}
                                    onChange={e => setWebhook({ requestBody: e.target.value })}
                                />
                            )}
                        </Field>
                    )}

                    <DynamicRows
                        title="Save response into variables"
                        showTopDivider={false}
                        showBottomDivider={false}
                        data={(webhook.responseMapping || []).map(inputs => ({ inputs }))}
                        onChange={rows => setWebhook({ responseMapping: rows.map(r => r.inputs) })}
                        rowsTemplate={(values, handleInputChange) => (
                            <div className="flex w-full gap-2">
                                <TextBox rootClass="flex-1" label="Response field" name="key" placeholder="data.status" value={values.key || ''} onChange={handleInputChange} />
                                <DropDown
                                    className="flex-1"
                                    label="Variable"
                                    name="slot"
                                    placeHolder="Choose a variable"
                                    value={values.slot || ''}
                                    options={slotOptions || []}
                                    onChange={handleInputChange}
                                />
                            </div>
                        )}
                    />

                    <ResultDelivery cfg={cfg} set={set} allowModifyByAI />
                    <PauseAfterExecutionSwitch
                        formData={cfg}
                        setFormData={next => set({ pauseAfterExecution: !!next.pauseAfterExecution })}
                        pauseAfterExecution={!!cfg.pauseAfterExecution}
                    />
                </>
            )}

            {step.kind === STEP_KIND.RUN && target === RUN_TARGET.KNOWLEDGE && (
                <>
                    <Field label="Limit to these knowledge bases" hint="All of them if left empty">
                        {/* MultiSelect's list is absolutely positioned against
                            its nearest positioned ancestor - anchor it here. */}
                        <div className="relative">
                            <MultiSelect
                                id="filterKb"
                                selectedOptions={filterKb}
                                options={knowledgeList || []}
                                onChangeOptions={onChangeKb}
                            />
                        </div>
                    </Field>
                    <SwitchComponent
                        title="Read the whole file"
                        subTitle="Answer by reading the selected knowledge bases' files instead of searching passages"
                        enabled={!!cfg.readFile}
                        onChange={readFile => set({ readFile })}
                    />
                    {!cfg.readFile && (
                        <Field label="How many passages to read" hint="Default 20">
                            <TextBox
                                type="number"
                                name="topK"
                                value={cfg.topK ?? 20}
                                onChange={e => set({ topK: Number(e.target.value) || 20 })}
                            />
                        </Field>
                    )}
                    <Field label="Instruction" hint="Optional">
                        <TextArea
                            name="systemPrompt"
                            rows={3}
                            placeholder="Eg: answer only from the delivery policy"
                            value={cfg.systemPrompt || ''}
                            onChange={onField}
                        />
                    </Field>
                    <Field label="Output format" hint="Optional">
                        <TextArea
                            name="outputFormat"
                            rows={2}
                            placeholder="Eg: yes or no, then one sentence"
                            value={cfg.outputFormat || ''}
                            onChange={onField}
                        />
                    </Field>
                    <ResultDelivery cfg={cfg} set={set} />
                </>
            )}

            {step.kind === STEP_KIND.RUN && target === RUN_TARGET.PROMPT && (
                <ResultDelivery cfg={cfg} set={set} />
            )}
        </div>
    );
}
